import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery with variant-aware slides.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    // Listen for variant updates
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // Listen for zoom selection
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, { signal });

    // Initialize active slides on page load
    this.#updateSlidesForVariant();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handle variant change event
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const { html, variantId } = event.detail.data;

    if (!html) return;
    const newGallery = html.querySelector('media-gallery');
    if (!newGallery) return;

    // Morph the gallery to the new content
    this.replaceWith(newGallery);

    // Update slider to show selected variant slides
    newGallery.#updateSlidesForVariant(variantId);
  };

  /**
   * Handle zoom selection
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Updates slides based on variant
   * @param {string} [variantId] - Optional variant ID; defaults to selected variant
   */
  #updateSlidesForVariant(variantId) {
    const slides = Array.from(this.media || []);
    if (!slides.length) return;

    // If no variantId is provided, find the first active variant slide
    if (!variantId) {
      const firstVariantSlide = slides.find(slide => slide.dataset.isVariantStart === 'true');
      variantId = firstVariantSlide?.dataset.variantStart || null;
    }

    if (!variantId) return;

    // Find the start index for this variant
    const startIndex = slides.findIndex(slide => slide.dataset.variantStart === variantId);

    // Find the end index (start of next variant) or end of slides
    let endIndex = slides.length;
    const nextVariantSlide = slides.find((s, i) => i > startIndex && s.dataset.isVariantStart === 'true');
    if (nextVariantSlide) endIndex = slides.indexOf(nextVariantSlide);

    // Mark slides as active/inactive for styling
    slides.forEach((slide, i) => {
      slide.dataset.active = i >= startIndex && i < endIndex ? 'true' : 'false';
    });

    // Move the slider to the first slide of this variant
    this.slideshow?.goToSlide(startIndex);
  }

  /**
   * Zoom media
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}